import { useMutation, useQuery } from '@apollo/client';
import { Box, DialogActions, DialogContent, Grid, MenuItem, styled } from '@mui/material';
import Loader from 'Components/Forms/Loader';
import TextFieldLimited from 'Components/Forms/TextFieldLimited';
import { mergeDefaults } from 'utils/form.utils';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import { TopBackground } from 'FeatureModules/ProductPage/Components/Create/styles';
import { createQuestionnaireSchema } from 'FeatureModules/Questionnaire/schemas/questionnaire.schema';
import { DialogDefault, FieldWrapper, ThemeButton } from 'DesignSystem';
import { Field, Form, Formik } from 'formik';
import { Select, TextField } from 'formik-mui';
import {
  CREATE_QUESTIONNAIRE,
  UPDATE_QUESTIONNAIRE,
} from 'graphql/secure/mutations/qustionnaire.mutations';
import {
  ICreateQuestionnaireInput,
  IUpdateQuestionnaireInput,
} from 'graphql/secure/mutations/types/questionnaire-mutation.types';
import { GET_QUESTIONNAIRE_TEMPLATES } from 'graphql/secure/queries/questionnaire.queries';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import React, { FC } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';
import {
  IQuestionnaire,
  IQuestionnaireTemplate,
} from 'FeatureModules/Questionnaire/types/questionnaire.types';
import { IGraphQlConnection } from 'types/core.types';
import { removeGraphConnections } from 'utils/graphql.utils';

interface IAddEditQuestionnaireDialogProps extends IDefaultDialogProps {
  questionnaire?: IQuestionnaire;
  onQuestionnaireCreated?: (questionnaire: IQuestionnaire) => void;
}

const StyledCard = styled(Grid)(({ theme }) => ({
  background: theme.custom.themeColors.white,
  boxShadow: theme.custom.shadows[4],
  borderRadius: 6,
  minHeight: 150,
  width: '100%',
  padding: theme.spacing(2),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(4, 0, 0, 0),
  justifyContent: 'space-between',
}));

const AddEditQuestionnaireDialog: FC<IAddEditQuestionnaireDialogProps> = ({
  open,
  questionnaire,
  onClose,
  onQuestionnaireCreated,
}) => {
  const { setSuccessMessage, setErrorMessage } = useMessages();
  const { logEvent } = useLogEvent();
  const { data, loading: templatesLoading } = useQuery<{
    questionnaireTemplates: IGraphQlConnection<IQuestionnaireTemplate>;
  }>(GET_QUESTIONNAIRE_TEMPLATES);

  const [createQuestionnaire] = useMutation<
    { createQuestionnaire: IQuestionnaire },
    { input: ICreateQuestionnaireInput }
  >(CREATE_QUESTIONNAIRE, {
    onCompleted: () => {
      setSuccessMessage('Questionnaire successfully created.');
    },
    onError: () => {
      setErrorMessage('Error creating questionnaire');
    },
    refetchQueries: ['GetQuestionnaires'],
  });

  const [updateQuestionnaire] = useMutation<
    { updateQuestionnaire: IQuestionnaire },
    { id: string; input: IUpdateQuestionnaireInput }
  >(UPDATE_QUESTIONNAIRE, {
    onCompleted: () => {
      setSuccessMessage('Questionnaire successfully updated.');
    },
    onError: () => {
      setErrorMessage('Error updating questionnaire');
    },
    refetchQueries: ['GetQuestionnaires', 'GetQuestionnaire'],
  });

  const questionnaireTemplates = data?.questionnaireTemplates
    ? removeGraphConnections(data.questionnaireTemplates)
    : [];

  const initialValues = mergeDefaults(createQuestionnaireSchema.default(), {
    title: questionnaire?.title,
    externalId: questionnaire?.externalId,
    year: questionnaire?.year || new Date().getFullYear(),
    templateId: questionnaire?.templateId || questionnaireTemplates[0]?.id,
  });

  const isEdit = !!questionnaire;

  const handleSubmitForm = async (values: ICreateQuestionnaireInput) => {
    if (isEdit) {
      await updateQuestionnaire({
        variables: {
          id: questionnaire.id,
          // Formik always changes the year to string, so we need to convert it back to number
          input: { ...omit(values, 'templateId'), year: Number(values.year) },
        },
      });
      logEvent('QUESTIONNAIRE_UPDATED');
    } else {
      const createQuestionnaireResponse = await createQuestionnaire({
        // Formik always changes the year to string, so we need to convert it back to number
        variables: { input: { ...values, year: Number(values.year) } },
      });
      logEvent('QUESTIONNAIRE_CREATED');
      if (createQuestionnaireResponse.data?.createQuestionnaire) {
        onQuestionnaireCreated?.(createQuestionnaireResponse.data.createQuestionnaire);
      }
    }
    onClose?.();
  };

  return (
    <DialogDefault
      title={isEdit ? 'Edit questionnaire' : 'Add new questionnaire'}
      onClose={onClose}
      open={open}
      fullWidth
      iconName="plus"
      maxWidth="md"
    >
      <TopBackground />
      <DialogContent sx={{ zIndex: 1 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={createQuestionnaireSchema}
          enableReinitialize
          onSubmit={handleSubmitForm}
        >
          {({ isValid, values }) => (
            <Form>
              <StyledCard container rowSpacing={2}>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FieldWrapper fullWidth label="Title" required>
                        <Field
                          name="title"
                          fullWidth
                          placeholder="E.g. Self-assessment 2025"
                          component={TextFieldLimited}
                          charLimit={50}
                          inputProps={{
                            'data-testid': 'field-questionnaire-title',
                          }}
                        />
                      </FieldWrapper>
                    </Grid>
                    <Grid item xs={6}>
                      <FieldWrapper
                        label="Internal ID"
                        tooltip={{
                          helperText:
                            'This should be an unique identifier used to track and manage the questionnaire.',
                          variant: 'INFO',
                        }}
                      >
                        <Field
                          component={TextField}
                          placeholder="1234567"
                          fullWidth
                          name="externalId"
                        />
                      </FieldWrapper>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FieldWrapper
                        label="Type"
                        required
                        fullWidth
                        tooltip={{
                          variant: 'INFO',
                          helperText:
                            'Select the type of questionnaire. This will decide what questions and response options that are included.',
                        }}
                      >
                        {templatesLoading ? (
                          <Box position="relative">
                            <Loader size={16} />
                          </Box>
                        ) : (
                          <Field
                            component={Select}
                            disabled={isEdit}
                            placeholder="Self-assessment"
                            formControl={{ fullWidth: true }}
                            name="templateId"
                          >
                            {questionnaireTemplates.map(({ id, title }) => (
                              <MenuItem key={id} value={id}>
                                {title}
                              </MenuItem>
                            ))}
                          </Field>
                        )}
                      </FieldWrapper>
                    </Grid>
                    <Grid item xs={6}>
                      <FieldWrapper
                        label="Year"
                        required
                        fullWidth
                        tooltip={{
                          variant: 'INFO',
                          helperText:
                            'Select the year for this questionnaire. Each questionnaire and their responses applies to a certain calendar year.',
                        }}
                      >
                        <Field
                          component={Select}
                          // Can not be changed after
                          disabled={isEdit}
                          placeholder="Select year of the assessment"
                          formControl={{ fullWidth: true }}
                          name="year"
                        >
                          {Array.from({ length: new Date().getFullYear() - 1973 }, (_, i) => {
                            const year = new Date().getFullYear() + 1 - i;
                            return (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            );
                          })}
                        </Field>
                      </FieldWrapper>
                    </Grid>
                  </Grid>
                </Grid>
              </StyledCard>

              <StyledDialogActions>
                <ThemeButton color="BLUE_ICE" size="large" type="button" onClick={onClose}>
                  Cancel
                </ThemeButton>
                <ThemeButton
                  color="PRIMARY"
                  size="large"
                  type="submit"
                  disabled={!isValid || isEqual(initialValues, values)}
                >
                  Save
                </ThemeButton>
              </StyledDialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </DialogDefault>
  );
};

export default AddEditQuestionnaireDialog;
